import React from "react"
import { ui } from "../../../shared/styles/uiSystem"
import SectionHeading from "../../../shared/components/SectionHeading"
import SubscriptionFeatureAccess from "../../subscriptions/components/SubscriptionFeatureAccess"
import {
  MetricChip,
  Tag,
  EmptyState,
  CollapseButton
} from "./DashboardAtoms"

export default function SingleClinicExecutiveSection({
  effectiveCanExecutive,
  currentPlanName,
  openUpgrade,
  singleClinicScoreRow,
  getScoreBadge,
  summary,
  formatMoney,
  formatPct,
  effectiveCanAdvancedReports,
  collapsedSections,
  toggleSection,
  singleClinicReadings,
  loading
}) {
  const readings = Array.isArray(singleClinicReadings) ? singleClinicReadings : []

  const shouldRenderReadingsSection =
    !effectiveCanAdvancedReports || loading || readings.length > 0

  const summaryNarrative = buildSingleClinicNarrative({
    summary,
    singleClinicScoreRow,
    formatMoney,
    formatPct
  })

  const focusText = buildSingleClinicFocusText({
    summary,
    singleClinicScoreRow,
    formatMoney,
    formatPct
  })

  const badge = singleClinicScoreRow
    ? getScoreBadge(singleClinicScoreRow.status)
    : null

  return (
    <>
      <SubscriptionFeatureAccess
        enabled={effectiveCanExecutive}
        featureKey="executiveDashboard"
        planName={currentPlanName}
        subtitle="Obtén una lectura gerencial más clara para tomar decisiones."
        onUpgrade={() => openUpgrade("executiveDashboard")}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 16,
            marginBottom: 16,
            alignItems: "start"
          }}
        >
          <div
            style={{
              ...ui.card,
              padding: 14,
              display: "grid",
              gap: 12,
              alignContent: "start"
            }}
          >
            <SectionHeading
              title="Salud del negocio hoy"
              subtitle="Lectura ejecutiva de la clínica"
            />

            {singleClinicScoreRow ? (
              <div
                style={{
                  padding: 14,
                  borderRadius: 16,
                  border: `1px solid ${badge.border}`,
                  background: "#ffffff",
                  boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)"
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    gap: 12,
                    alignItems: "center",
                    flexWrap: "wrap",
                    marginBottom: 10
                  }}
                >
                  <div style={{ fontWeight: 900, fontSize: 16, color: "#0f172a" }}>
                    {singleClinicScoreRow.clinicName}
                  </div>

                  <div
                    style={{
                      padding: "6px 10px",
                      borderRadius: 999,
                      background: badge.background,
                      color: badge.color,
                      border: `1px solid ${badge.border}`,
                      fontWeight: 900,
                      fontSize: 12
                    }}
                  >
                    {badge.label} · Score {singleClinicScoreRow.score}
                  </div>
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                    gap: 10,
                    marginBottom: 10
                  }}
                >
                  <MetricChip label="Producción" value={`$${formatMoney(singleClinicScoreRow.production)}`} />
                  <MetricChip label="Cobrado" value={`$${formatMoney(singleClinicScoreRow.collected)}`} />
                  <MetricChip label="Pendiente" value={`$${formatMoney(singleClinicScoreRow.pending)}`} />
                  <MetricChip label="Ocupación" value={`${formatPct(singleClinicScoreRow.occupationPct)}%`} />
                </div>

                {!!singleClinicScoreRow.reasons?.length && (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {singleClinicScoreRow.reasons.map((reason, index) => (
                      <Tag key={index}>{reason}</Tag>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
                  gap: 10
                }}
              >
                <MetricChip label="Producción" value={`$${formatMoney(summary.production)}`} />
                <MetricChip label="Cobrado" value={`$${formatMoney(summary.collected)}`} />
                <MetricChip label="Pendiente" value={`$${formatMoney(summary.pending)}`} />
                <MetricChip label="Ocupación" value={`${formatPct(summary.avgOccupation)}%`} />
              </div>
            )}

            <CompactNarrativeCard text={summaryNarrative} />
          </div>

          <div
            style={{
              ...ui.card,
              padding: 14,
              display: "grid",
              gap: 12,
              alignContent: "start"
            }}
          >
            <SectionHeading
              title="Foco del día"
              subtitle="Qué conviene mirar primero"
            />

            <FocusCard text={focusText} />

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: 10
              }}
            >
              <MetricChip
                label="Cobrado hoy"
                value={`$${formatMoney(summary.collected)}`}
              />
              <MetricChip
                label="Cartera pendiente"
                value={`$${formatMoney(summary.pending)}`}
              />
              <MetricChip
                label="Citas del día"
                value={formatCount(summary.appointments)}
              />
              <MetricChip
                label="Ocupación"
                value={`${formatPct(summary.avgOccupation)}%`}
              />
            </div>
          </div>
        </div>
      </SubscriptionFeatureAccess>

      {shouldRenderReadingsSection && (
        <SubscriptionFeatureAccess
          enabled={effectiveCanAdvancedReports}
          featureKey="advancedReports"
          planName={currentPlanName}
          subtitle="Desbloquea interpretación del negocio y recomendaciones de nivel gerencial."
          onUpgrade={() => openUpgrade("advancedReports")}
        >
          <div style={{ ...ui.card, marginBottom: 16, padding: 14 }}>
            <SectionHeading
              title="Lecturas ejecutivas"
              subtitle="Interpretación del negocio para una clínica"
              actions={
                <CollapseButton
                  collapsed={!!collapsedSections.singleClinicReadings}
                  onClick={() => toggleSection("singleClinicReadings")}
                />
              }
            />

            {!collapsedSections.singleClinicReadings && (
              <>
                {!readings.length ? (
                  <EmptyState loading={loading} text="Sin lecturas disponibles para esta fecha." />
                ) : (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
                      gap: 10
                    }}
                  >
                    {readings.map((reading, index) => (
                      <ReadingCard
                        key={index}
                        index={index}
                        text={reading}
                      />
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </SubscriptionFeatureAccess>
      )}
    </>
  )
}

function CompactNarrativeCard({ text }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        color: "#334155",
        fontSize: 13,
        lineHeight: 1.45
      }}
    >
      {text}
    </div>
  )
}

function FocusCard({ text }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background:
          "linear-gradient(180deg, rgba(239,246,255,0.95) 0%, rgba(248,250,252,0.96) 100%)",
        border: "1px solid #dbeafe",
        color: "#1e293b",
        fontSize: 13,
        lineHeight: 1.45,
        fontWeight: 700
      }}
    >
      {text}
    </div>
  )
}

function ReadingCard({ index, text }) {
  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        background: "#f8fafc",
        border: "1px solid #e5e7eb",
        display: "grid",
        gap: 6
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 800,
          color: "#64748b",
          textTransform: "uppercase",
          letterSpacing: 0.3
        }}
      >
        Lectura {index + 1}
      </div>

      <div
        style={{
          color: "#334155",
          fontSize: 13,
          lineHeight: 1.45
        }}
      >
        {text}
      </div>
    </div>
  )
}

function buildSingleClinicNarrative({
  summary,
  singleClinicScoreRow,
  formatMoney,
  formatPct
}) {
  if (singleClinicScoreRow?.pending > 0 && singleClinicScoreRow?.collected <= 0) {
    return `La clínica tiene cartera pendiente por $${formatMoney(singleClinicScoreRow.pending)} y cobranza en 0%. Conviene atacar seguimiento de pagos hoy.`
  }

  if ((singleClinicScoreRow?.occupationPct ?? summary?.avgOccupation ?? 0) < 40) {
    return `La ocupación está en ${formatPct(singleClinicScoreRow?.occupationPct ?? summary?.avgOccupation ?? 0)}%. Hay espacio para reactivación, recall o agenda recuperable.`
  }

  return "La clínica opera con lectura estable. Mantén seguimiento sobre cobranza, producción y ocupación para sostener el ritmo del día."
}

function buildSingleClinicFocusText({
  summary,
  singleClinicScoreRow,
  formatMoney,
  formatPct
}) {
  const pending = singleClinicScoreRow?.pending ?? summary?.pending ?? 0
  const collected = singleClinicScoreRow?.collected ?? summary?.collected ?? 0
  const occupation = singleClinicScoreRow?.occupationPct ?? summary?.avgOccupation ?? 0

  if (pending > 0 && collected <= 0) {
    return `Foco en cobranza: hay $${formatMoney(pending)} pendientes y todavía no entra cobro del día.`
  }

  if (occupation < 40) {
    return `Foco en agenda: la ocupación está en ${formatPct(occupation)}%. Buen momento para recall, reactivación o campañas.`
  }

  return "Foco en continuidad: la clínica va estable. Revisa conversión de tratamientos y seguimiento de pagos para no perder ritmo."
}

function formatCount(value) {
  const parsed = Number(value)
  const safe = Number.isFinite(parsed) ? parsed : 0

  return new Intl.NumberFormat("es-MX", {
    maximumFractionDigits: 0
  }).format(safe)
}